import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  TrendingUp, Shield, Target, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock, XCircle, 
  BookOpen, BarChart3, Activity, DollarSign, LineChart
} from 'lucide-react';

const faqData = [
  {
    question: "Long pozisyon ne demek?",
    answer: "Long, bir varligin fiyatinin yukselecegi beklentisiyle acilan pozisyondur. Dusuk fiyattan alinir, yuksek fiyattan satilir."
  },
  {
    question: "Long islem nasil yapilir?",
    answer: "Futures veya spot piyasada alis yonunde pozisyon acilir. Giris, TP ve Stop seviyeleri belirlenir."
  },
  {
    question: "Long pozisyonda nasil zarar edilir?",
    answer: "Fiyat giris seviyesinin altina dustugunde zarar olusur. Stop Loss bu zarari sinirlar."
  },
  {
    question: "Long ve alis ayni sey midir?",
    answer: "Spot piyasada evet. Futures piyasada Long, kaldiracli yukselis pozisyonu anlamina gelir."
  },
  {
    question: "Long pozisyon ne zaman acilir?",
    answer: "Teknik ve temel analiz yukselis sinyali verdiginde degerlendirilir. Kesinlik icermez, olasilik temellidir."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Long Nedir? Kripto Piyasasinda Long Pozisyon Rehberi",
    "description": "Long pozisyon nedir, nasil calisir? Kripto futures islemlerinde long acma mantigi, kaldirac, risk yonetimi ve ornek senaryolar.",
    "author": { "@type": "Organization", "name": "Borsamex" },
    "publisher": { "@type": "Organization", "name": "Borsamex", "logo": { "@type": "ImageObject", "url": "https://borsamex.com/logo512.png" } },
    "datePublished": "2026-04-02",
    "dateModified": "2026-04-02",
    "mainEntityOfPage": "https://borsamex.com/rehber/long-nedir"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const LongNedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Long Nedir? Kripto Piyasasinda Long Pozisyon Rehberi | Borsamex"
        description="Long pozisyon nedir, nasil calisir? Kripto futures islemlerinde long acma mantigi, kaldirac, risk yonetimi ve ornek senaryolar. Yeni baslayanlar icin detayli rehber."
        keywords="long nedir, long pozisyon nedir, long islem nedir, kripto long, long acmak ne demek, long pozisyon nasil acilir, bitcoin long"
        canonical="https://borsamex.com/rehber/long-nedir"
        structuredData={structuredData}
      />

      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Long Nedir?</span>
          </div>
        </div>
      </div>

      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-green-500/10 text-green-400 border-green-500/20">Trading</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>8 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Long Nedir? Kripto Piyasasinda Long Pozisyon
            </h1>
            
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-green-400">Long pozisyon</strong>, bir varligin fiyatinin yukselecegi beklentisiyle acilan islem yonudur. 
                Trader dusuk fiyattan pozisyon acar ve fiyat yukseldiginde senaryo olumlu sonuclanir.
              </p>
              <p className="text-slate-400 mt-4">
                Long, kripto piyasasinin en temel islem konseptlerinden biridir ve hem spot hem futures piyasada kullanilir.
              </p>
            </div>
          </motion.header>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="prose prose-invert prose-slate max-w-none">
            
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-green-400" />
                </div>
                Long Pozisyon Ne Demek?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Long (uzun pozisyon), bir varligin degerinin artacagi beklentisiyle yapilan islemdir. En basit haliyle "dusukten al, yuksekten sat" mantigina dayanir.
              </p>
              
              <p className="text-slate-300 mb-4">
                Kripto piyasasinda long pozisyon iki sekilde acilabilir:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Card className="border-green-500/30 bg-green-950/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-5 w-5 text-green-400" />
                      <span className="font-bold text-white">Spot Long</span>
                    </div>
                    <p className="text-slate-300 text-sm">Varlik dogrudan satin alinir. Fiyat arttiginda satilarak kar elde edilir. Kaldirac yoktur.</p>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-500/30 bg-blue-950/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <LineChart className="h-5 w-5 text-blue-400" />
                      <span className="font-bold text-white">Futures Long</span>
                    </div>
                    <p className="text-slate-300 text-sm">Kaldiracli pozisyon acilir. Daha az sermaye ile buyuk pozisyon alinabilir, ancak risk de artar.</p>
                  </CardContent>
                </Card>
              </div>
              
              <p className="text-slate-400">
                Her iki yontem de fiyat artisina dayali senaryo icin kullanilir.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-blue-400" />
                </div>
                Long Islem Nasil Calisir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Bir long islemin temel adimlari:
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mt-0.5">1</Badge>
                  <div>
                    <p className="text-white font-medium">Analiz</p>
                    <p className="text-slate-400 text-sm">Teknik ve temel analiz ile yukselis potansiyeli degerlendirilir</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mt-0.5">2</Badge>
                  <div>
                    <p className="text-white font-medium">Giris Seviyesi</p>
                    <p className="text-slate-400 text-sm">Uygun giris noktasi belirlenir (destek bolgesi, kirilma noktasi vb.)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mt-0.5">3</Badge>
                  <div>
                    <p className="text-white font-medium">TP ve Stop Loss</p>
                    <p className="text-slate-400 text-sm">Hedef fiyat (TP) ve risk siniri (Stop Loss) tanimlanir</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mt-0.5">4</Badge>
                  <div>
                    <p className="text-white font-medium">Pozisyon Yonetimi</p>
                    <p className="text-slate-400 text-sm">Fiyat hareketi izlenir, gerektiginde kademeli cikis yapilir</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Inline AI Sinyal Kutusu */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Ornegi</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">BTC-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      Giris: $67,450 | TP1: $69,200 | TP2: $71,000 | Stop: $66,100
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Bu analizleri manuel yapmak yerine AI destekli sinyalleri inceleyebilirsin</span>
                    </div>
                  </div>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Sinyalleri Gor <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                </div>
                Long Pozisyonda Kaldirac
              </h2>
              
              <p className="text-slate-300 mb-4">
                Futures piyasada long pozisyon kaldiracli acilabilir. Kaldirac, mevcut sermayenin kati kadar pozisyon acmayi saglar.
              </p>
              
              <p className="text-slate-300 mb-4">Ornek:</p>
              
              <Card className="border-amber-500/30 bg-amber-950/20 mb-6">
                <CardContent className="p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-slate-300">
                      <span>Sermaye:</span>
                      <span className="text-white font-medium">$1,000</span>
                    </div>
                    <div className="flex justify-between text-slate-300">
                      <span>Kaldirac:</span>
                      <span className="text-amber-400 font-medium">10x</span>
                    </div>
                    <div className="flex justify-between text-slate-300">
                      <span>Pozisyon Buyuklugu:</span>
                      <span className="text-white font-medium">$10,000</span>
                    </div>
                    <div className="border-t border-slate-700/50 pt-2 mt-2">
                      <div className="flex justify-between text-slate-300">
                        <span>Fiyat %5 artarsa:</span>
                        <span className="text-green-400 font-medium">+$500 (%50 kar)</span>
                      </div>
                      <div className="flex justify-between text-slate-300">
                        <span>Fiyat %5 duserse:</span>
                        <span className="text-red-400 font-medium">-$500 (%50 zarar)</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex items-start gap-3 text-slate-300 mb-4">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <span>Kaldirac hem kari hem zarari buyutur. Yeni baslayanlar icin dusuk kaldirac (2x-5x) onerilir.</span>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-red-400" />
                </div>
                Long Pozisyonda Risk Yonetimi
              </h2>
              
              <p className="text-slate-300 mb-4">
                Long pozisyonda en buyuk risk, fiyatin beklentinin tersine dusmesidir. Bu nedenle:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Stop Loss zorunludur</strong> - Senaryonun gecersiz oldugu seviyeyi belirler</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Pozisyon boyutu</strong> - Toplam sermayenin %2-5'inden fazlasi riske edilmemeli</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Kademeli TP</strong> - Tek hedef yerine birden fazla TP seviyesi kullanilmali</span>
                </li>
              </ul>

              <div className="flex gap-4">
                <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi">
                  <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    Risk Yonetimi Rehberi
                  </Button>
                </Link>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-purple-400" />
                </div>
                Long Pozisyon Ne Zaman Acilir?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Long pozisyon icin yaygin kullanilan sinyaller:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">RSI asiri satim bolgesi</strong> - RSI 30 altina dustugunde potansiyel donus sinyali</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">MACD bullish crossover</strong> - Sinyal cizgisi yukari kesisimi</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Destek bolgesi</strong> - Fiyat guclu destek seviyesinden donus yaptiginda</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span><strong className="text-white">Fibonacci retracement</strong> - %61.8 veya %50 geri cekilme tamamlandiginda</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                Bu sinyaller AI trading sistemlerinde otomatik olarak analiz edilir ve long sinyal uretiminde kullanilir.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-brand-green" />
                </div>
                Borsamex'te Long Sinyaller
              </h2>
              
              <p className="text-slate-300 mb-4">Borsamex AI sistemi long sinyallerinde:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>200+ teknik gostergeyi analiz eder</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Giris, TP ve Stop seviyelerini otomatik hesaplar</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk/odul oranini optimize eder</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>AI aciklama ile senaryo baglami sunar</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-8 w-8 text-amber-400 shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-amber-400 mb-2">Onemli Uyari</h3>
                      <p className="text-slate-300">
                        Long pozisyon acmak yatirim tavsiyesi degildir. Kripto piyasasi yuksek volatilite icerir. 
                        Tum islemler risk barindirir ve sermaye kaybi yasanabilir.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-blue-400" />
                </div>
                Ilgili Makaleler
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/rehber/short-nedir" className="group">
                  <Card className="border-red-500/20 bg-slate-900/50 hover:border-red-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-red-400 rotate-180" />
                        <div>
                          <p className="font-medium text-white group-hover:text-red-400 transition-colors">Short Nedir?</p>
                          <p className="text-xs text-slate-500">Dusus yonlu pozisyonlar</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/futures-trading-nedir" className="group">
                  <Card className="border-purple-500/20 bg-slate-900/50 hover:border-purple-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <LineChart className="h-5 w-5 text-purple-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-purple-400 transition-colors">Futures Trading Nedir?</p>
                          <p className="text-xs text-slate-500">Vadeli islemler rehberi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/tp-ve-stop-loss-nedir" className="group">
                  <Card className="border-green-500/20 bg-slate-900/50 hover:border-green-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Target className="h-5 w-5 text-green-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-green-400 transition-colors">TP ve Stop Loss Nedir?</p>
                          <p className="text-xs text-slate-500">Hedef ve risk seviyeleri</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/rsi-nedir" className="group">
                  <Card className="border-blue-500/20 bg-slate-900/50 hover:border-blue-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="h-5 w-5 text-blue-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-blue-400 transition-colors">RSI Nedir?</p>
                          <p className="text-xs text-slate-500">Asiri alim-satim gostergesi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sik Sorulan Sorular</h2>
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <ShareBox 
              title="Long Nedir? Kripto Long Pozisyon Rehberi"
              url="/rehber/long-nedir"
              hashtags="#kripto #long #bitcoin #trading"
            />

          </motion.div>

          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/destek-direnc-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Destek ve Direnc Nedir?
            </Link>
            <Link to="/rehber/short-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Sonraki: Short Nedir?
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-green-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              Long Pozisyon Mantigini Ogrendin
            </h3>
            <p className="text-slate-400 mb-6">
              Simdi canli long sinyallerini takip et ve AI destekli analizleri incele.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ucretsiz Basla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default LongNedirPage;
